import { InputAdornment, TextField } from '@material-ui/core';
import React, { useRef } from 'react';
import { useDialogState } from '../../../hooks/useDialogState';
import { now, Timestamp, toMoment } from '../../../services/time';
import { DateTimeDialog } from '../DateTimeDialog';

const dateTimeWidth = 230;

export const DateTimeField = ({
  value,
  onChange,
  tz,
  format = 'MM/DD/YYYY hh:mm A',
  autoFocus,
  fullWidth,
  confirmLabel,
  disablePast,
  disableFuture,
  label,
  minutes,
  minutesStep,
  error,
  hideTz
}: {
  tz: string;
  format?: string;
  autoFocus?: boolean;
  fullWidth?: boolean;
  confirmLabel?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  label?: React.ReactNode;
  value: Timestamp | null;
  onChange: (nextTimestamp: Timestamp) => void;
  minutes?: boolean;
  minutesStep?: number;
  error?: boolean;
  hideTz?: boolean;
}) => {
  const v = useRef(0);
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const open = () => {
    v.current++;
    openDialog();
  };
  return (
    <>
      <TextField
        label={label}
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        value={value ? toMoment(value).tz(tz).format(format) : ''}
        onClick={open}
        variant="outlined"
        error={error}
        InputProps={{
          readOnly: true,
          endAdornment: hideTz ? null : (
            <InputAdornment position="end">
              {value ? toMoment(value).tz(tz).format('z') : ''}
            </InputAdornment>
          ),
          style: { width: fullWidth ? 'auto' : dateTimeWidth }
        }}
      />
      <DateTimeDialog
        key={v.current}
        open={dialogOpen}
        onClose={closeDialog}
        value={value}
        onConfirm={onChange}
        clearable={false}
        confirmLabel={confirmLabel}
        tz={tz}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minutes={minutes}
        minutesStep={minutesStep}
      />
    </>
  );
};

export const DateTimeFieldNullable = ({
  value,
  onChange,
  tz,
  format = 'YYYY/MM/DD HH:mm',
  autoFocus,
  fullWidth,
  confirmLabel,
  disablePast,
  disableFuture,
  label,
  minutes
}: {
  value: Timestamp | null;
  onChange: (nextTimestamp: Timestamp | null) => void;
  tz: string;
  format?: string;
  autoFocus?: boolean;
  fullWidth?: boolean;
  confirmLabel?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  label?: React.ReactNode;
  minutes?: boolean;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  return (
    <>
      <TextField
        label={label}
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        value={value ? toMoment(value).tz(tz).format(format) : ''}
        onClick={openDialog}
        variant="outlined"
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {value ? toMoment(value).tz(tz).format('z') : ''}
            </InputAdornment>
          ),
          style: { width: fullWidth ? 'auto' : dateTimeWidth }
        }}
      />
      <DateTimeDialog
        open={dialogOpen}
        onClose={closeDialog}
        clearable={true}
        value={value || now()}
        onConfirm={onChange}
        confirmLabel={confirmLabel}
        tz={tz}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minutes={minutes}
      />
    </>
  );
};
